/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect, useState } from "react";
import LandingPageTeaser from "@/src/components/Teaser/LandingPageTeaser";
import Container from "@/src/components/Container/Container";
import LpAdvocardMeta from "./LpAdvocardMeta";
import AdvocardFlexCheckin, { CreateFlexCaseRequest } from "@/src/components/Checkin/AdvocardFlexCheckin/AdvocardFlexCheckin";
import LayoutAdvocardFlex from "./LayoutAdvocardFlex";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import HowItWorksSectionAdvocardFlex from "./HowItWorksSectionAdvocardFlex";
import FlexAiChatAgent from "./FlexAiChatAgent";
import { Button, Link as MuiLink } from "@mui/material";
import LpAdvocardFlexStyle from "./LpAdvocardFlexStyle";
import { AdvocardFlexAiResponse } from "@/src/types/AdvocardFlexAiResponse";
import { Link } from "react-router";

const LpAdvocardFlex = () => {
  const [isValidUser, setIsValidUser] = useState(false);
  const [createCaseRequest, setCreateCaseRequest] = useState<CreateFlexCaseRequest | null>(null);
  const [aiResponse, setAiResponse] = useState<null | AdvocardFlexAiResponse>(null);

  useEffect(() => {
    if (isValidUser) {
      window.scrollTo(0, 0);
    }
  }, [isValidUser]);

  const validateCallback = (success: boolean, createRequest: CreateFlexCaseRequest) => {
    if (success) {
      setIsValidUser(true);
      setCreateCaseRequest(createRequest);
    }
  };

  return (
    <LayoutAdvocardFlex>
      <LpAdvocardMeta title={"ADVOCARD-360°-FlexService"} description={"Der Immer-Aktuell-Service zur Beantwortung rechtlicher Fragen"} />
      {!isValidUser && (
        <>
          <LandingPageTeaser
            teaserButtonText={"Jetzt starten"}
            headline={" ADVOCARD-360°- FlexService"}
            secondHeadline={"Der Immer-Aktuell-Service zur Beantwortung rechtlicher Fragen"}
            subheadline={
              <>
                Legalbird bietet Ihnen als ADVOCARD-360°-Kunden präventive Unterstützung und zielgerichtete Informationen zu wechselnden aktuellen Themen. Der
                Service ist in Ihrem Rechtsschutzbeitrag enthalten.
                <br />
                Sind Sie kein ADVOCARD-360°-Kunde? Dann rufen Sie uns einfach an:{" "}
                <MuiLink href={"tel:040237310"} underline={"none"}>
                  <strong>040 237310</strong>
                </MuiLink>
              </>
            }
            teaserButtonLink={"#top-checkin"}
          >
            <Container>
              <Grid
                container
                wrap={"nowrap"}
                sx={{
                  alignContent: "center",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Grid size={12}>
                  <Typography
                    variant={"h3"}
                    sx={{
                      textAlign: "center",
                      pb: 3,
                    }}
                  >
                    Aktuell: Auswirkungen internationaler Krisenlagen auf Reisen & Verträge
                  </Typography>
                  <Typography
                    variant={"subtitle2"}
                    sx={{
                      textAlign: "center",
                    }}
                  >
                    Unvorhersehbare Ereignisse können zu Reiseausfällen und -abbrüchen oder Preiserhöhung bei der Energieversorgung führen. <br />
                    Doch wer trägt die Kosten? Welche Rechte gelten bei stornierten Reisen oder Preisanpassungen aufgrund steigender Energiepreise? Jetzt alle
                    Fragen klären!
                  </Typography>
                </Grid>
              </Grid>
            </Container>
          </LandingPageTeaser>
          <HowItWorksSectionAdvocardFlex />
          <Container>
            <AdvocardFlexCheckin idPrefix={"top"} mode={"validate"} validateCallback={validateCallback} />
          </Container>
        </>
      )}
      {isValidUser && createCaseRequest && (
        <>
          <LandingPageTeaser
            hasTeaserButton={false}
            headline={"ADVOCARD-360°- FlexService"}
            secondHeadline={"Der Immer-Aktuell-Service zur Beantwortung rechtlicher Fragen"}
            subheadline={""}
          >
            <Container sx={{ p: 0 }}>
              <Grid
                container
                spacing={2}
                size={12}
                sx={{
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <Grid
                  size={{
                    xs: 12,
                    sm: "auto",
                  }}
                >
                  <Button component={Link} variant="contained" to="#frage-stellen" sx={LpAdvocardFlexStyle.button}>
                    Ihre Frage stellen
                  </Button>
                </Grid>
              </Grid>
              <Grid
                container
                wrap={"nowrap"}
                sx={{
                  alignContent: "center",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Grid size={12}>
                  <Typography
                    variant={"h3"}
                    sx={{
                      textAlign: "center",
                      pb: 3,
                    }}
                  >
                    Aktuell: Auswirkungen internationaler Krisenlagen auf Reisen & Verträge
                  </Typography>
                  <Typography
                    variant={"subtitle2"}
                    sx={{
                      textAlign: "center",
                    }}
                  >
                    Ihr Problem mit Auswirkungen internationaler Krisenlagen - jetzt offene Fragen direkt hier klären.
                  </Typography>
                  <Grid size={12}>
                    <FlexAiChatAgent
                      postalCode={createCaseRequest.postalCode}
                      email={createCaseRequest.email}
                      insurancePolicyNumber={createCaseRequest.insurancePolicyNumber}
                      aiResponse={aiResponse}
                      setAiResponse={setAiResponse}
                      setCreateCaseRequest={setCreateCaseRequest}
                      questionLabel={"Ihre Frage zum Thema Auswirkungen internationaler Krisenlagen auf Reisen & Verträge:"}
                      placeholder={
                        "Mein Flug über Dubai wurde wegen der Luftraumsperrungen im Nahen Osten annulliert. Gelten hier 'außergewöhnliche Umstände' und welche Rechte habe ich bei einer Pauschalreise bezüglich einer Stornierung?"
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Container>
          </LandingPageTeaser>
        </>
      )}
    </LayoutAdvocardFlex>
  );
};

export default LpAdvocardFlex;
